import type { BrokerId } from '@shared/broker-types.js';
import { BROKER_IDS, isBrokerId } from '@shared/broker-types.js';
import {
  isPaperTradingEnabled,
  setPaperTradingEnabled,
} from './paper-trading.js';

const BROKER_STORAGE_KEY = 'momo-broker-v1';
/** Confirmed active mode: paper trading or a live broker. Survives refresh. */
const BROKER_MODE_STORAGE_KEY = 'momo-broker-mode-v1';
export const BROKER_CHANGED_EVENT = 'momo-broker-changed';
export const IB_READY_EVENT = 'momo-ib-ready-changed';

export type BrokerMode = 'paper' | BrokerId;

/** True only after Settings confirms gateway authenticated:true. */
let ibSessionReady = false;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore quota / private-mode errors.
  }
}

export function getSelectedBroker(): BrokerId {
  const stored = readStorage(BROKER_STORAGE_KEY);
  return isBrokerId(stored) ? stored : 'schwab';
}

export function setSelectedBroker(broker: BrokerId): void {
  if (!BROKER_IDS.includes(broker)) return;
  writeStorage(BROKER_STORAGE_KEY, broker);
  if (broker !== 'ib') {
    setIbSessionReady(false);
  }
  window.dispatchEvent(new CustomEvent(BROKER_CHANGED_EVENT, { detail: { broker } }));
}

/**
 * Last confirmed broker mode (paper vs live broker).
 * Live paper flag wins over a stale live-broker mode key (e.g. flag set while
 * mode key still says schwab). Mode key `paper` still wins when the flag is
 * temporarily cleared mid Schwab-connect.
 */
export function getBrokerMode(): BrokerMode {
  if (isPaperTradingEnabled()) return 'paper';
  const stored = readStorage(BROKER_MODE_STORAGE_KEY);
  if (stored === 'paper') return 'paper';
  if (isBrokerId(stored)) return stored;
  return getSelectedBroker();
}

/** Persist the confirmed mode and keep paper/broker keys in sync. */
export function setBrokerMode(mode: BrokerMode): void {
  writeStorage(BROKER_MODE_STORAGE_KEY, mode);

  if (mode === 'paper') {
    setPaperTradingEnabled(true);
    // PAPER_MODE_EVENT already notifies listeners; also fire broker-changed so
    // UIs that only watch BROKER_CHANGED_EVENT stay in sync.
    window.dispatchEvent(new CustomEvent(BROKER_CHANGED_EVENT, { detail: { broker: 'paper' } }));
    return;
  }

  setPaperTradingEnabled(false);
  setSelectedBroker(mode);
}

/**
 * Reconcile legacy flags with the confirmed mode after refresh.
 * Fixes mid-flow cases where paper was temporarily disabled for Schwab connect.
 */
export function restoreBrokerMode(): BrokerMode {
  const mode = getBrokerMode();
  // Ensure the mode key exists going forward.
  writeStorage(BROKER_MODE_STORAGE_KEY, mode);

  if (mode === 'paper') {
    if (!isPaperTradingEnabled()) setPaperTradingEnabled(true);
  } else {
    if (isPaperTradingEnabled()) setPaperTradingEnabled(false);
    if (getSelectedBroker() !== mode) setSelectedBroker(mode);
  }

  return mode;
}

export function isIbSessionReady(): boolean {
  return ibSessionReady;
}

export function setIbSessionReady(ready: boolean): void {
  if (ibSessionReady === ready) return;
  ibSessionReady = ready;
  window.dispatchEvent(new CustomEvent(IB_READY_EVENT, { detail: { ready } }));
}

/**
 * Broker header for trading requests.
 * Until IB auth is confirmed, do not send broker=ib — polling an unauthenticated
 * gateway during browser SSO causes CP_LOGIN_FAILED / validate Access Denied.
 */
export function brokerHeaders(): HeadersInit {
  const broker = getSelectedBroker();
  if (broker === 'ib' && !ibSessionReady) {
    return {};
  }
  return { 'X-Momo-Broker': broker };
}

export function getTradingBroker(): BrokerId {
  const broker = getSelectedBroker();
  if (broker === 'ib' && !ibSessionReady) {
    return 'schwab';
  }
  return broker;
}

export function assertIbReadyForTrading(): void {
  if (getSelectedBroker() === 'ib' && !ibSessionReady) {
    throw new Error(
      'IB Gateway not connected yet. Keep Settings on Schwab (or closed) while you log in at https://localhost:5001, then click Refresh IB status.',
    );
  }
}
